import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, ListItem, ListItemButton, Switch, Tooltip, Typography, styled } from "@mui/material";
import FormSubmitButton from "@/components/forms/FormSubmitButton";
import Form from "@/components/forms/Form";
import Select, { ISelectItem } from "@/components/forms/inputs/Select";
import { ConfigurationSourceData, ConfigurationSourceDataAllocation, Institution, Participant, ParticipantSourceDataAllocationUpdate } from "@/api/dtos";
import { IReturn } from "@servicestack/client";
import { GroupedVirtuoso } from "react-virtuoso";
import { ComponentProps, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Checkbox from "@/components/forms/inputs/Checkbox";
import { DateTime } from "luxon";
import { IFormSubmit, InputComponentType, OmitInputProps, useFormActions, useScopedField } from "@ngt/forms-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FormError } from "@/errors";
import { AppSettings } from "@/AppSettings";
import { DefaultSourceDataFormSettings, ISourceDataFormSettings } from "@/contexts/form/SourceDataFormSettingsContext";


const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogTitle-root': {
        padding: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'space-between',
        alignItems: 'center',

        '& > div': {
            display: 'flex',
            alignItems: 'center'
        }
    },
    '& .MuiDialogContent-root': {
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(0,-3),
}));


const FormButtons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'
}));

interface ISourceDataFormSettingsDialogProps {
    formData?: ISourceDataFormSettings;
    open?: boolean;
    onClose?: () => void;
    onSave?: (data?: ISourceDataFormSettings ) => Promise<any>;
}

const ButtonSeparationDisplay = () => {
    const { state: { value: separateButtons } } = useScopedField<boolean>('separateButtons', { value: true });

    if (separateButtons) {
        return (
            <Box
                sx={{
                    minWidth: 170,
                    marginLeft: 'auto'
                }}
            >
                <Button 
                    variant="outlined" 
                    sx={{minWidth: 46, minHeight: 46, padding: 0, mr: 2}}
                >
                        <FontAwesomeIcon icon={AppSettings.icons.audit} size="2x"/>
                </Button>
                <Button 
                    variant="outlined" 
                    sx={{minWidth: 46, minHeight: 46, padding: 0, mr: 2}}
                >
                        <FontAwesomeIcon icon={AppSettings.icons.note} size="2x"/>
                </Button>
                <Button 
                    variant="outlined" 
                    sx={{minWidth: 46, minHeight: 46, padding: 0}}
                >
                        <FontAwesomeIcon icon={AppSettings.icons.finding} size="2x"/>
                </Button>
            </Box>
        )
    }


    return (
        <Box
            sx={{
                minWidth: 50,
                marginLeft: 'auto'
            }}
        >
            <Button 
                variant="outlined" 
                sx={{minWidth: 46, minHeight: 46, padding: 0}}
            >
                    <FontAwesomeIcon icon={AppSettings.icons.combined} size="2x"/>
            </Button>
        </Box>
    )
}

const rowSizeOptions = [
    {
        id: 'small',
        name: 'Small'
    },
    {
        id: 'normal',
        name: 'Normal'
    },
    {
        id: 'large',
        name: 'Large'
    }
]

const buttonModeOptions = [
    {
        id: 'compact',
        name: 'Compact'
    },
    {
        id: 'separate',
        name: 'Expanded'
    }
]

const buttonLinkTargetOptions = [
    {
        id: 'current',
        name: 'Current Tab'
    },
    {
        id: 'tab',
        name: 'New Tab'
    },
    {
        id: 'window',
        name: 'New Window'
    }
]

const SourceDataFormSettingsDialog: FC<ISourceDataFormSettingsDialogProps> = ({
    formData,
    open,
    onClose,
    onSave
}) => {
    const submit = useCallback<IFormSubmit<ISourceDataFormSettings, FormError>>(async (formState, formActions) => {
        await onSave?.(formState.value ?? {...DefaultSourceDataFormSettings});

        onClose?.();

        return {};
    }, [onSave, onClose]);

    return (
        <Form
            initialValue={formData ?? DefaultSourceDataFormSettings}
            onSubmit={submit}
            debug={AppSettings.debug}
        >
            <StyledDialog
                //onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open ?? false}
                maxWidth="xs"
                fullWidth
                scroll="paper"
            >
                <DialogTitle>
                    Form Settings
                </DialogTitle>
                <DialogContent dividers>
                    <Typography
                        variant="subtitle1"
                        color='primary'
                        gutterBottom
                    >
                        Button Link Target
                    </Typography>
                    <Typography
                        variant="body1"
                        gutterBottom
                    >
                        The button link target determines where the links for Notes, Queries, Findings & Audits will open.
                    </Typography>
                    <InputOnlyField
                        propertyPath="buttonLinkTarget"
                        label="Mode"
                        component={Select}
                        options={buttonLinkTargetOptions}
                        nullOption={false}
                        //parse={(inputValue) => inputValue == 'separate'}
                        convert={(fieldValue) => !fieldValue ? 'current' : fieldValue}
                        sx={{
                            mt: 2,
                            mb: 3
                        }}
                    />
                    <StyledDivider />
                    <Typography
                        variant="subtitle1"
                        color='primary'
                        gutterBottom
                    >
                        Button Separation
                    </Typography>
                    <Typography
                        variant="body1"
                        gutterBottom
                    >
                        The compact triple dot button can be split into individual buttons to provide easier display of query and finding counts at the cost of consuming more screen space.
                    </Typography>
                    <InputOnlyField
                        propertyPath="separateButtons"
                        label="Mode"
                        component={Select}
                        options={buttonModeOptions}
                        nullOption={false}
                        parse={(inputValue) => inputValue == 'separate'}
                        convert={(fieldValue) => fieldValue === true ? 'separate' : 'compact'}
                        sx={{
                            mt: 2,
                            mb: 3
                        }}
                    />
                    <StyledDivider />
                    <Typography
                        sx={{
                            mt: 2
                        }}
                        variant="subtitle1"
                        color='primary'
                        gutterBottom
                    >
                        Button/Element Visibility
                    </Typography>
                    <Typography
                        variant="body1"
                        gutterBottom
                    >
                        Unused buttons and elements can be hidden to free up more screen space.
                    </Typography>
                    <Typography
                        variant="body1"
                    >
                        Select the buttons and elements you wish to hide below:
                    </Typography>
                    <InputOnlyField 
                        propertyPath="hidePriorityIcon"
                        label="Priority Icons"
                        component={Checkbox}
                    />
                    <InputOnlyField 
                        propertyPath="hideAudit"
                        label="Audits"
                        component={Checkbox}
                    />
                    <InputOnlyField 
                        propertyPath="hideNotesAndQueries"
                        label="Notes & Queries"
                        component={Checkbox}
                    />
                    <InputOnlyField 
                        propertyPath="hideFindings"
                        label="Findings"
                        component={Checkbox}
                    />
                    <InputOnlyField 
                        propertyPath="hideReviews"
                        label="Reviews"
                        component={Checkbox}
                    />
                    <InputOnlyField 
                        propertyPath="hideVerifications"
                        label="Verifications"
                        component={Checkbox}
                    />
                    <StyledDivider />
                    <Typography
                        sx={{
                            mt: 2
                        }}
                        variant="subtitle1"
                        color='primary'
                        gutterBottom
                    >
                        OpenClinica Links
                    </Typography>
                    <Typography
                        variant="body1"
                        gutterBottom
                    >
                        OpenClinica stores a context (trial and institution) against the user's current session, this context is used by OpenClinica when displaying forms and other information.
                    </Typography>
                    <Typography
                        variant="body1"
                        gutterBottom
                    >
                        To receive the expected behaviour when clicking on an OpenClinica link, ensure that the appropriate context is selected in OpenClinica. 
                    </Typography>
                    <Typography
                        variant="body1"
                    >
                        The warning shown when clicking an OpenClinica link can be hidden by selecting the option below.
                    </Typography>
                    <InputOnlyField 
                        propertyPath="hideOpenClinicaLinkWarning"
                        label="Hide OpenClinica Context Warning"
                        component={Checkbox}
                    />
                </DialogContent>
                <DialogActions>
                        <FormButtons>
                            <FormSubmitButton
                                variant="contained"
                                color="primary"
                            >
                                Ok
                            </FormSubmitButton>
                            { 
                                onClose && (
                                    <Button
                                        variant="contained"
                                        color="inherit"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </Button>
                                )
                            }
                        </FormButtons>
                </DialogActions>
            </StyledDialog>
        </Form>
    );
}

export default SourceDataFormSettingsDialog;